// apps/web/src/pages/author/constraint-config/visibility-config.tsx
//
// Phase 9 Plan 07 Task 2 — Visibility constraint authoring sub-form
// (CSTR-01 partial + CSTR-05 explanation surface).
//
// D-16 generalization (replaces the legacy summary-checklist `visibleWhen`
// shape): this sub-form targets the GENERALIZED visibility Constraint AST per
// packages/shared/src/schemas/constraint.ts lines 52-60:
//   { kind: "visibility";
//     show: StateRef { widgetInstanceId, pathInState };
//     when: { ref: StateRef, equals: unknown };
//     explanation: 1-500 chars }
//
// equals editor — v1 per D-22 + Q-09 resolution (plan body line 100): a plain
// Input + a "Parse as JSON" Checkbox toggle. When the toggle is on, the value
// is JSON.parse'd before being stored in form-state; a parse failure surfaces
// a local error message. The typed combobox-from-widget-option-ids
// enhancement is documented as a Phase 13 polish item.
//
// UI-SPEC §"Visibility" copy table (lines 487-492):
//   Helper: "Shows or hides this widget based on the state of another widget."
//   Target label: "Show this widget"
//   Migration tooltip on Kind header: "Visibility rules now support nested
//     conditions. Existing summary-checklist rules migrated automatically."
//
// authr-06 hygiene: per-path useWatch ONLY. NEVER top-level.

import { Info } from "lucide-react";
import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ConstraintConfigSubFormProps } from "./types.js";

// =============================================================================
// VisibilityConfigShape — local typing for the default factory.
// =============================================================================

export interface VisibilityConfigShape {
  kind: "visibility";
  show: { widgetInstanceId: string; pathInState: string };
  when: {
    ref: { widgetInstanceId: string; pathInState: string };
    equals: unknown;
  };
  explanation: string;
}

const MIGRATION_TOOLTIP =
  "Visibility rules now support nested conditions. Existing summary-checklist rules migrated automatically.";

// =============================================================================
// Default factory
// =============================================================================

export function defaultVisibilityConfig(): VisibilityConfigShape {
  return {
    kind: "visibility",
    show: { widgetInstanceId: "", pathInState: "" },
    when: {
      ref: { widgetInstanceId: "", pathInState: "" },
      equals: "",
    },
    explanation: "",
  };
}

// =============================================================================
// EqualsEditor — shared between visibility + dependency.
// Internal helper component (not exported) — kept in this file for module
// cohesion. dependency-config.tsx mirrors the same idiom.
// =============================================================================

interface EqualsEditorProps {
  /** RHF form path to the `equals` field (e.g. "constraints.0.when.equals"). */
  path: string;
  /** test-id prefix — distinguishes visibility vs dependency in test queries. */
  testIdPrefix: string;
  /** Visible label for the equals Input. */
  label: string;
}

function EqualsEditor({ path, testIdPrefix, label }: EqualsEditorProps): React.ReactElement {
  const { control, setValue } = useFormContext();
  // Per-path useWatch — narrow subscription to this equals value.
  const stored = useWatch({ control, name: path }) as unknown;

  // Local UI state for the JSON parse toggle + the text view.
  const [parseAsJson, setParseAsJson] = React.useState(false);
  const [parseError, setParseError] = React.useState<string | null>(null);

  // Convert stored value → displayed text. When parseAsJson is on, stringify
  // the stored value; otherwise show as string.
  const displayedText = React.useMemo(() => {
    if (stored === undefined || stored === null) return "";
    if (typeof stored === "string") return stored;
    try {
      return JSON.stringify(stored);
    } catch {
      return String(stored);
    }
  }, [stored]);

  const handleChange = React.useCallback(
    (raw: string) => {
      if (parseAsJson && raw !== "") {
        try {
          const parsed: unknown = JSON.parse(raw);
          setValue(path, parsed, { shouldDirty: true, shouldTouch: true });
          setParseError(null);
        } catch (err) {
          setParseError(err instanceof Error ? err.message : "Invalid JSON");
          // Still store the raw text so the user doesn't lose their input.
          setValue(path, raw, { shouldDirty: true, shouldTouch: true });
        }
      } else {
        setValue(path, raw, { shouldDirty: true, shouldTouch: true });
        setParseError(null);
      }
    },
    [parseAsJson, path, setValue],
  );

  const toggleId = `${testIdPrefix}-json-toggle`;
  const inputId = `${testIdPrefix}-input`;

  return (
    <div className="flex flex-col gap-2" data-field-path={path}>
      <Label htmlFor={inputId}>{label}</Label>
      <Input
        id={inputId}
        data-testid={testIdPrefix}
        value={displayedText}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={parseAsJson ? '{"key":"value"}' : "value"}
      />
      {parseError !== null ? (
        <p className="text-sm text-destructive">JSON parse error: {parseError}</p>
      ) : null}
      <div className="flex items-center gap-2">
        <Checkbox
          id={toggleId}
          data-testid={toggleId}
          checked={parseAsJson}
          onCheckedChange={(checked) => {
            setParseAsJson(checked === true);
            setParseError(null);
          }}
        />
        <label htmlFor={toggleId} className="text-xs text-muted-foreground cursor-pointer">
          Parse as JSON
        </label>
      </div>
    </div>
  );
}
EqualsEditor.displayName = "EqualsEditor";

// Re-exported so dependency-config.tsx can reuse the identical editor.
export { EqualsEditor };

// =============================================================================
// VisibilityConfigSubForm
// =============================================================================

export function VisibilityConfigSubForm({
  constraintId: _constraintId,
  basePath,
}: ConstraintConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="VISIBILITY">
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground flex-1">
            Shows or hides this widget based on the state of another widget.
          </p>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid="visibility-migration-tooltip"
                aria-label={MIGRATION_TOOLTIP}
                className="text-muted-foreground hover:text-foreground"
              >
                <Info className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{MIGRATION_TOOLTIP}</TooltipContent>
          </Tooltip>
        </div>

        <Fieldset legend="SHOW WIDGET">
          <FormField
            control={control}
            name={`${basePath}.show.widgetInstanceId`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.show.widgetInstanceId`}>
                <FormLabel>Show this widget</FormLabel>
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="widget-instance-id"
                    data-testid="visibility-show-widget"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${basePath}.show.pathInState`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.show.pathInState`}>
                <FormLabel>at this state path</FormLabel>
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="visible"
                    data-testid="visibility-show-path"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Fieldset>

        <Fieldset legend="WHEN SOURCE WIDGET'S STATE EQUALS">
          <FormField
            control={control}
            name={`${basePath}.when.ref.widgetInstanceId`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.when.ref.widgetInstanceId`}>
                <FormLabel>When this widget's state</FormLabel>
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="widget-instance-id"
                    data-testid="visibility-when-widget"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${basePath}.when.ref.pathInState`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.when.ref.pathInState`}>
                <FormLabel>at this path</FormLabel>
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="selectedFlavors"
                    data-testid="visibility-when-path"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <EqualsEditor
            path={`${basePath}.when.equals`}
            testIdPrefix="visibility-equals"
            label="equals"
          />
        </Fieldset>
      </Fieldset>

      <Fieldset legend="EXPLANATION">
        <FormField
          control={control}
          name={`${basePath}.explanation`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.explanation`}>
              <FormLabel>Explanation</FormLabel>
              <FormControl>
                <Textarea
                  {...f}
                  value={(f.value as string | undefined) ?? ""}
                  rows={3}
                  maxLength={500}
                  placeholder="Shown to authors and viewers when this rule disables an option. Required."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>
    </div>
  );
}
VisibilityConfigSubForm.displayName = "VisibilityConfigSubForm";
